import React, { useEffect, useState } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Eye, EyeOff, Loader2, GraduationCap, BookOpen, School, Heart, CheckCircle2, Clock, Plus, X as XIcon } from 'lucide-react';
import axios from 'axios';
import { useAuth } from '../context/AuthContext';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { GlassCard } from '../components/shared';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const ROLES = [
  {
    id: 'student',
    label: 'Étudiant',
    icon: GraduationCap,
    description: "Apprends à ton rythme, accès immédiat à toutes les leçons.",
  },
  {
    id: 'teacher',
    label: 'Professeur',
    icon: BookOpen,
    description: "Encadre des élèves dans une madrassa partenaire.",
  },
  {
    id: 'madrassa',
    label: 'Madrassa',
    icon: School,
    description: "Inscris ton école et gère tes professeurs et élèves.",
  },
  {
    id: 'parent',
    label: 'Parent',
    icon: Heart,
    description: "Suis la progression de tes enfants et gère leur compte.",
  },
];

const Register = () => {
  const navigate = useNavigate();
  const { register } = useAuth();
  const [searchParams] = useSearchParams();
  const inviteToken = searchParams.get('invite');

  // Common fields
  const [role, setRole] = useState('student');
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);

  // Teacher fields
  const [madrassas, setMadrassas] = useState([]);
  const [selectedMadrassaId, setSelectedMadrassaId] = useState('');

  // Madrassa fields
  const [mName, setMName] = useState('');
  const [mCity, setMCity] = useState('');
  const [mCountry, setMCountry] = useState('France');
  const [mPhone, setMPhone] = useState('');

  // Parent fields
  const [childrenEmails, setChildrenEmails] = useState(['']);
  const addChildEmail = () => setChildrenEmails((arr) => [...arr, '']);
  const updateChildEmail = (i, v) => setChildrenEmails((arr) => arr.map((x, idx) => (idx === i ? v : x)));
  const removeChildEmail = (i) => setChildrenEmails((arr) => arr.filter((_, idx) => idx !== i));

  // Invitation context (overrides role)
  const [invite, setInvite] = useState(null);
  const [inviteError, setInviteError] = useState('');

  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(null); // {kind: "teacher_pending" | "madrassa_pending"}

  useEffect(() => {
    if (!inviteToken) return;
    axios.get(`${API_URL}/api/auth/invitations/${inviteToken}`)
      .then((r) => {
        setInvite(r.data);
        setRole(r.data.role || 'teacher');
        if (r.data.email) setEmail(r.data.email);
        if (r.data.madrassa_id) setSelectedMadrassaId(r.data.madrassa_id);
      })
      .catch((err) => {
        setInviteError(err?.response?.data?.detail || "Lien d'invitation invalide.");
      });
  }, [inviteToken]);

  useEffect(() => {
    if (role !== 'teacher' || invite) return;
    axios.get(`${API_URL}/api/auth/onboarding/madrassas`)
      .then((r) => setMadrassas(r.data || []))
      .catch(() => setMadrassas([]));
  }, [role, invite]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');

    if (password !== confirmPassword) {
      setError("Les mots de passe ne correspondent pas.");
      return;
    }
    if (password.length < 6) {
      setError("Le mot de passe doit contenir au moins 6 caractères.");
      return;
    }
    if (role === 'teacher' && !invite && !selectedMadrassaId) {
      setError("Sélectionnez votre madrassa.");
      return;
    }
    if (role === 'madrassa') {
      if (!mName.trim() || !mCity.trim() || !mCountry.trim()) {
        setError("Renseignez le nom, la ville et le pays de la madrassa.");
        return;
      }
    }

    setLoading(true);
    // Hide keyboard before attempting registration to improve UX
    document.activeElement?.blur();
    
    const body = { name, email, password, role };
    if (invite) body.invitation_token = invite.token;
    if (role === 'teacher' && !invite) body.madrassa_id = selectedMadrassaId;
    if (role === 'madrassa') {
      body.madrassa_info = {
        name: mName.trim(), city: mCity.trim(), country: mCountry.trim(),
        phone: mPhone.trim() || null,
      };
    }
    if (role === 'parent') {
      body.children_emails = childrenEmails.map((e) => e.trim()).filter(Boolean);
    }
    const result = await register(body);
    setLoading(false);

    if (!result.success) {
      // Re-show keyboard on registration failure so user can correct fields
      setError(result.error);
      document.getElementById('name')?.focus();
      return;
    }
    const pendingKind = result.user?.pending_kind;
    if (pendingKind) {
      setSuccess({ kind: pendingKind });
      return;
    }
    navigate('/', { replace: true });
  };

  if (success) {
    return <PendingValidationCard kind={success.kind} email={email} onContinue={() => navigate('/')} />;
  }

  if (inviteError) {
    return (
      <div className="min-h-screen flex items-center justify-center px-4 py-12">
        <GlassCard className="max-w-md w-full text-center">
          <h2 className="text-xl font-serif text-text-primary mb-3">Lien d'invitation</h2>
          <p className="text-danger text-sm mb-4">{inviteError}</p>
          <Link to="/register" className="text-primary hover:underline" data-testid="register-without-invite">
            Continuer sans invitation
          </Link>
        </GlassCard>
      </div>
    );
  }

  return (
    <div className="min-h-screen flex items-center justify-center px-4 py-12">
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        className="w-full max-w-2xl"
      >
        <div className="text-center mb-6">
          <img src="/logo-192.png" alt="Noor El Bayen" className="w-16 h-16 mx-auto mb-4 rounded-xl" draggable="false" />
          <h1 className="text-2xl font-serif text-text-primary mb-2">Créer un compte</h1>
          <p className="text-text-secondary text-sm">
            {invite
              ? <>Vous avez été invité à rejoindre <strong>{invite.madrassa_name}</strong> en tant que <strong>professeur</strong>.</>
              : "Choisissez votre profil pour commencer."}
          </p>
        </div>

        {!invite && (
          <div className="grid grid-cols-2 sm:grid-cols-2 lg:grid-cols-4 gap-3 mb-6" data-testid="signup-role-selector">
            {ROLES.map((r) => {
              const Icon = r.icon;
              const active = role === r.id;
              return (
                <button
                  key={r.id}
                  type="button"
                  onClick={() => setRole(r.id)}
                  data-testid={`signup-role-${r.id}`}
                  data-role-card={r.id}
                  className={`p-4 rounded-2xl border text-left transition-all ${
                    active
                      ? 'bg-primary/15 border-primary text-text-primary shadow-[0_0_0_1px_rgba(248,210,113,0.4)]'
                      : 'bg-surface/40 border-border hover:border-primary/40'
                  }`}
                >
                  <div className="flex items-center gap-2 mb-2">
                    <Icon className={`w-5 h-5 ${active ? 'text-primary' : 'text-text-muted'}`} />
                    <span className="font-medium">{r.label}</span>
                  </div>
                  <p className="text-xs text-text-muted leading-snug">{r.description}</p>
                </button>
              );
            })}
          </div>
        )}

        <GlassCard>
          <form onSubmit={handleSubmit} className="space-y-5">
            {error && (
              <div className="p-3 rounded-lg bg-danger/20 border border-danger/50 text-danger text-sm" data-testid="register-error">
                {error}
              </div>
            )}

            <Field id="name" label="Nom complet" value={name} onChange={setName} required testid="register-name-input" />
            <Field id="email" type="email" label="Email" value={email} onChange={setEmail} required disabled={!!invite?.email} testid="register-email-input" />

            {role === 'teacher' && !invite && (
              <div className="space-y-2">
                <Label htmlFor="madrassa-select" className="text-text-primary">Votre madrassa</Label>
                <select
                  id="madrassa-select"
                  value={selectedMadrassaId}
                  onChange={(e) => setSelectedMadrassaId(e.target.value)}
                  required
                  data-testid="register-madrassa-select"
                  className="w-full bg-surface border border-border rounded-md px-3 py-2 text-text-primary"
                >
                  <option value="">— Sélectionnez —</option>
                  {madrassas.map((m) => (
                    <option key={m.id} value={m.id}>
                      {m.name}{m.city ? ` (${m.city})` : ''}
                    </option>
                  ))}
                </select>
                <p className="text-xs text-text-muted">
                  Votre madrassa devra approuver votre inscription. Si vous avez reçu un lien d'invitation, utilisez-le à la place.
                </p>
              </div>
            )}

            {role === 'madrassa' && (
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <Field id="m-name" label="Nom de la madrassa" value={mName} onChange={setMName} required testid="register-madrassa-name" />
                <Field id="m-city" label="Ville" value={mCity} onChange={setMCity} required testid="register-madrassa-city" />
                <Field id="m-country" label="Pays" value={mCountry} onChange={setMCountry} required testid="register-madrassa-country" />
                <Field id="m-phone" label="Téléphone (optionnel)" value={mPhone} onChange={setMPhone} testid="register-madrassa-phone" />
              </div>
            )}

            {role === 'parent' && (
              <div className="space-y-3">
                <Label className="text-text-primary">Email(s) de votre/vos enfant(s) déjà inscrit(s)</Label>
                {childrenEmails.map((em, i) => (
                  <div key={i} className="flex gap-2">
                    <Input
                      type="email"
                      value={em}
                      onChange={(e) => updateChildEmail(i, e.target.value)}
                      placeholder="enfant@example.com"
                      className="bg-surface border-border flex-1"
                      data-testid={`register-child-email-${i}`}
                    />
                    {childrenEmails.length > 1 && (
                      <Button type="button" variant="ghost" size="icon"
                              onClick={() => removeChildEmail(i)}
                              className="text-rose-400 hover:bg-rose-500/10"
                              data-testid={`register-child-remove-${i}`}>
                        <XIcon className="w-4 h-4" />
                      </Button>
                    )}
                  </div>
                ))}
                <Button type="button" variant="outline" size="sm" onClick={addChildEmail}
                        data-testid="register-child-add">
                  <Plus className="w-4 h-4 mr-1" /> Ajouter un enfant
                </Button>
                <p className="text-xs text-text-muted leading-relaxed">
                  Chaque enfant recevra un email pour confirmer le lien parental.<br />
                  Si un autre parent est déjà rattaché, vous serez automatiquement liés en tant que co-parents.
                </p>
              </div>
            )}

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="password" className="text-text-primary">Mot de passe</Label>
                <div className="relative">
                  <Input
                    id="password"
                    type={showPassword ? 'text' : 'password'}
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    placeholder="••••••••"
                    required
                    className="bg-surface border-border pr-10"
                    data-testid="register-password-input"
                  />
                  <button type="button" onClick={() => setShowPassword(!showPassword)}
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-text-muted hover:text-text-primary">
                    {showPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                  </button>
                </div>
              </div>
              <Field id="confirm-password" type="password" label="Confirmer" value={confirmPassword} onChange={setConfirmPassword} required testid="register-confirm-password-input" />
            </div>

            <Button type="submit" disabled={loading}
              className="w-full bg-primary text-background hover:bg-primary-hover"
              data-testid="register-submit-button">
              {loading ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : null}
              {role === 'student' && "S'inscrire"}
              {role === 'teacher' && (invite ? "Accepter l'invitation" : "Envoyer ma demande")}
              {role === 'madrassa' && "Soumettre la demande de madrassa"}
            </Button>
          </form>

          <div className="mt-6 text-center text-sm text-text-secondary">
            Déjà un compte ?{' '}
            <Link to="/login" className="text-primary hover:underline">
              Connexion
            </Link>
          </div>
        </GlassCard>
      </motion.div>
    </div>
  );
};

const Field = ({ id, label, value, onChange, type = 'text', required = false, disabled = false, testid }) => (
  <div className="space-y-2">
    <Label htmlFor={id} className="text-text-primary">{label}</Label>
    <Input
      id={id}
      type={type}
      value={value}
      onChange={(e) => onChange(e.target.value)}
      required={required}
      disabled={disabled}
      className="bg-surface border-border"
      data-testid={testid}
    />
  </div>
);

const PendingValidationCard = ({ kind, email, onContinue }) => {
  const isTeacher = kind === 'teacher';
  return (
    <div className="min-h-screen flex items-center justify-center px-4 py-12">
      <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} className="w-full max-w-md">
        <GlassCard>
          <div className="text-center">
            <div className="w-16 h-16 rounded-full bg-primary/20 flex items-center justify-center mx-auto mb-5">
              <Clock className="w-8 h-8 text-primary" />
            </div>
            <h2 className="text-2xl font-serif text-text-primary mb-3">Demande envoyée 🌙</h2>
            <p className="text-text-secondary text-sm mb-5 leading-relaxed">
              {isTeacher
                ? "Votre demande a été transmise à l'administrateur de la madrassa. Vous recevrez un email dès qu'elle sera traitée."
                : "Votre demande de création de madrassa a été enregistrée. Notre équipe l'examinera sous quelques jours et vous notifiera par email."}
            </p>
            <div className="bg-surface/50 border border-border rounded-lg p-3 mb-5 text-xs text-text-muted flex items-center gap-2 justify-center">
              <CheckCircle2 className="w-4 h-4 text-primary" /> Email de confirmation envoyé à <strong>{email}</strong>
            </div>
            <Button onClick={onContinue} data-testid="pending-continue-btn"
                    className="w-full bg-primary text-background hover:bg-primary-hover">
              Découvrir l'application en attendant
            </Button>
          </div>
        </GlassCard>
      </motion.div>
    </div>
  );
};

export default Register;
